
import React from 'react';
import { useApp } from '../store';
import { useNavigate } from 'react-router-dom';
import { User, MapPin, Shield, Star, LogOut, Settings, Bell, CreditCard, ChevronLeft, Hammer, Ruler } from 'lucide-react';

const Profile: React.FC = () => {
  const { currentUser, setCurrentUser } = useApp();
  const navigate = useNavigate();

  if (!currentUser) return null;

  const handleLogout = () => {
    setCurrentUser(null);
    navigate('/');
  };

  return (
    <div className="space-y-8 max-w-xl mx-auto px-4 reveal">
      <div className="flex items-center gap-5 mb-4">
        <button 
          onClick={() => navigate(-1)} 
          className="p-3 bg-white border border-stone-200 hover:bg-stone-50 rounded-xl text-stone-600 transition-all shadow-sm"
        >
          <ChevronLeft size={24} />
        </button>
        <h2 className="text-4xl font-black text-[#2d1b0f] tracking-tight font-serif">Workshop ID</h2>
      </div>

      {/* Main Profile Card */}
      <div className="bg-[#3d2b1f] rounded-[3rem] p-10 shadow-2xl border border-amber-900/40 text-center relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-full h-full wood-grain opacity-10 pointer-events-none"></div>
        
        <div className="relative z-10">
          <div className="w-28 h-28 bg-amber-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-amber-400 border-4 border-amber-600 shadow-2xl group-hover:rotate-6 transition-transform">
            <User size={56} />
          </div>
          
          <h2 className="text-3xl font-black text-amber-50 tracking-tight font-serif italic">{currentUser.name}</h2>
          
          <div className="flex items-center justify-center gap-3 mt-3">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-400 bg-amber-500/10 px-4 py-1.5 rounded-full border border-amber-500/20">
              {currentUser.role}
            </span>
            <span className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.3em] text-stone-400">
              <MapPin size={14} className="text-rose-500" /> {currentUser.city}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-12">
            <div className="bg-black/20 backdrop-blur-sm p-6 rounded-3xl border border-white/5">
              <span className="text-[9px] font-black uppercase tracking-[0.4em] text-amber-100/30 block mb-2">Mastery Score</span>
              <div className="flex items-center justify-center gap-2 text-amber-500 font-black text-xl">
                <Star size={20} fill="currentColor" /> 4.9
              </div>
            </div>
            <div className="bg-black/20 backdrop-blur-sm p-6 rounded-3xl border border-white/5">
              <span className="text-[9px] font-black uppercase tracking-[0.4em] text-amber-100/30 block mb-2">Certification</span>
              <div className="flex items-center justify-center gap-2 text-emerald-400 font-black uppercase tracking-widest text-[10px]">
                <Shield size={20} /> Verified
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Options */}
      <div className="bg-white rounded-[2.5rem] p-4 shadow-sm border border-stone-100 divide-y divide-stone-50">
        <button className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-all rounded-3xl group">
          <div className="flex items-center gap-5">
            <div className="p-3 bg-blue-50 text-blue-700 rounded-2xl group-hover:bg-[#2d1b0f] group-hover:text-amber-400 transition-all">
              <Bell size={24} />
            </div>
            <span className="text-base font-black text-[#2d1b0f] font-serif">Workshop Alerts</span>
          </div>
          <span className="bg-amber-600 w-2.5 h-2.5 rounded-full shadow-[0_0_10px_rgba(217,119,6,0.6)]"></span>
        </button>
        
        <button className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-all rounded-3xl group">
          <div className="flex items-center gap-5">
            <div className="p-3 bg-emerald-50 text-emerald-700 rounded-2xl group-hover:bg-[#2d1b0f] group-hover:text-amber-400 transition-all">
              <CreditCard size={24} />
            </div>
            <span className="text-base font-black text-[#2d1b0f] font-serif">Platform Credits</span>
          </div>
          <ChevronLeft size={20} className="rotate-180 text-stone-200" />
        </button>

        <button className="w-full flex items-center justify-between p-5 hover:bg-stone-50 transition-all rounded-3xl group">
          <div className="flex items-center gap-5">
            <div className="p-3 bg-stone-100 text-stone-600 rounded-2xl group-hover:bg-[#2d1b0f] group-hover:text-amber-400 transition-all">
              <Settings size={24} />
            </div>
            <span className="text-base font-black text-[#2d1b0f] font-serif">Toolbox Settings</span>
          </div>
          <ChevronLeft size={20} className="rotate-180 text-stone-200" />
        </button>
      </div>

      <button 
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-4 p-6 bg-[#4a2c2a] text-amber-100 rounded-[2rem] font-black text-xs uppercase tracking-[0.4em] hover:bg-stone-900 transition-all shadow-2xl border-b-8 border-black/40 active:border-b-0 active:translate-y-2"
      >
        <LogOut size={24} />
        Close the Workshop
      </button>
      
      <div className="text-center pt-4 opacity-20">
        <Ruler className="mx-auto text-stone-900 mb-2" size={24} />
        <p className="text-[10px] font-black uppercase tracking-[0.5em]">Handlo Protocol v1.3</p>
      </div>
    </div>
  );
};

export default Profile;
